import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../../../../environments/environment';
import {
  IInventoryCategory,
  IInventoryItem,
  IInventoryItemCategory,
  IInventoryItemQuantity,
} from '../../../../models/inventory.model';
import { ISibasiHTTPResponse } from '../../../../models/sibasiHTTPResponse.model';

@Injectable({
  providedIn: 'root',
})
export class InventoryService {
  updatedInventoryItemQuantity = new BehaviorSubject<Boolean>(false);

  constructor(private httpClient: HttpClient) {}

  /* ####################################  INVENTORY ITEMS  #################################### */

  getAllInventoryItems(): Observable<ISibasiHTTPResponse<IInventoryItem[]>> {
    return this.httpClient.get<ISibasiHTTPResponse<IInventoryItem[]>>(
      `${environment.api_url}/inventory/item/all`
    );
  }

  getPaginatedInventoryItems(
    pagination?: any
  ): Observable<ISibasiHTTPResponse<IInventoryItem[]>> {
    return this.httpClient
      .post<ISibasiHTTPResponse<IInventoryItem[]>>(
        `${environment.api_url}/inventory/item/paginated`,
        pagination ? pagination : {}
      )
      .pipe(
        map((res) => {
          const inventoryItems =
            res.result?.length > 0 ? res.result.slice().reverse() : [];
          return {
            ...res,
            result: inventoryItems,
          };
        })
      );
  }

  getInventoryItemById(
    inventoryItemId: string
  ): Observable<ISibasiHTTPResponse<IInventoryItem>> {
    return this.httpClient.get<ISibasiHTTPResponse<IInventoryItem>>(
      `${environment.api_url}/inventory/item/one/${inventoryItemId}`
    );
  }

  createInventoryItem(
    InventoryItem: IInventoryItem
  ): Observable<ISibasiHTTPResponse<IInventoryItem>> {
    return this.httpClient.post<ISibasiHTTPResponse<IInventoryItem>>(
      `${environment.api_url}/inventory/item`,
      InventoryItem
    );
  }

  updateInventoryItem(
    InventoryItem: Partial<IInventoryItem>,
    InventoryItemId: string
  ): Observable<ISibasiHTTPResponse<IInventoryItem>> {
    return this.httpClient.post<ISibasiHTTPResponse<IInventoryItem>>(
      `${environment.api_url}/inventory/item/update/${InventoryItemId}`,
      InventoryItem
    );
  }

  // deleteInventoryItem(InventoryItemId: string): Observable<ISibasiHTTPResponse<IInventoryItem>> {
  //   return this.httpClient.delete<ISibasiHTTPResponse<IInventoryItem>>(`${environment.api_url}/Inventory/item/delete/${InventoryItemId}`);
  // }

  deleteInventoryItem(
    inventoryItemId: string
  ): Observable<ISibasiHTTPResponse<IInventoryCategory>> {
    return this.httpClient.delete<ISibasiHTTPResponse<IInventoryCategory>>(
      `${environment.api_url}/inventory/item/slateforDeletion/${inventoryItemId}`
    );
  }

  restoreDeletedInventoryItem(
    inventoryItemId: string
  ): Observable<ISibasiHTTPResponse<IInventoryCategory>> {
    return this.httpClient.delete<ISibasiHTTPResponse<IInventoryCategory>>(
      `${environment.api_url}/inventory/item/unslateforDeletion/${inventoryItemId}`
    );
  }

  getDeletedInventoryItem(): Observable<
    ISibasiHTTPResponse<IInventoryCategory>
  > {
    return this.httpClient.get<ISibasiHTTPResponse<IInventoryCategory>>(
      `${environment.api_url}/inventory/item/deleted/all`
    );
  }

  getAllInventoryItemsCount(
    inStock?: boolean
  ): Observable<ISibasiHTTPResponse<number>> {
    return this.httpClient.get<ISibasiHTTPResponse<number>>(
      `${environment.api_url}/inventory/item/all/count`
    );
  }

  getAllSearchedInventoryItemsCount(
    searchParam: string
  ): Observable<ISibasiHTTPResponse<number>> {
    return this.httpClient.post<ISibasiHTTPResponse<number>>(
      `${environment.api_url}/inventory/item/searched/count`,
      { searchParam }
    );
  }

  getFilteredInventoryItemsByInventoryItemCategory(
    searchParam
  ): Observable<ISibasiHTTPResponse<IInventoryItem[]>> {
    return this.httpClient.post<ISibasiHTTPResponse<IInventoryItem[]>>(
      `${environment.api_url}/inventory/item/category/filtered/all`,
      searchParam
    );
  }

  /* ####################################  INVENTORY LIST  #################################### */

  getAllInventoryList(): Observable<ISibasiHTTPResponse<any[]>> {
    return this.httpClient.get<ISibasiHTTPResponse<any[]>>(
      `${environment.api_url}/inventory/all`
    );
  }

  getPaginatedInventoryList(
    pagination?: any
  ): Observable<ISibasiHTTPResponse<any[]>> {
    return this.httpClient
      .post<ISibasiHTTPResponse<any[]>>(
        `${environment.api_url}/inventory/paginated`,
        pagination ? pagination : {}
      )
      .pipe(
        map((res) => {
          const inventoryList =
            res.result?.length > 0 ? res.result.slice().reverse() : [];
          return {
            ...res,
            result: inventoryList,
          };
        })
      );
  }

  getInventoryById(inventoryId: string): Observable<ISibasiHTTPResponse<any>> {
    return this.httpClient.get<ISibasiHTTPResponse<any>>(
      `${environment.api_url}/inventory/one/${inventoryId}`
    );
  }

  createInventory(Inventory: any): Observable<ISibasiHTTPResponse<any>> {
    return this.httpClient.post<ISibasiHTTPResponse<any>>(
      `${environment.api_url}/inventory`,
      Inventory
    );
  }

  updateInventory(
    Inventory: Partial<any>,
    InventoryId: string
  ): Observable<ISibasiHTTPResponse<any>> {
    return this.httpClient.post<ISibasiHTTPResponse<any>>(
      `${environment.api_url}/inventory/update/${InventoryId}`,
      Inventory
    );
  }

  deleteInventory(InventoryId: string): Observable<ISibasiHTTPResponse<any>> {
    return this.httpClient.delete<ISibasiHTTPResponse<any>>(
      `${environment.api_url}/inventory/delete/${InventoryId}`
    );
  }

  getAllInventoryCount(): Observable<ISibasiHTTPResponse<number>> {
    return this.httpClient.get<ISibasiHTTPResponse<number>>(
      `${environment.api_url}/inventory/all/count`
    );
  }

  getAllSearchedInventoryCount(
    searchParam: string
  ): Observable<ISibasiHTTPResponse<number>> {
    return this.httpClient.post<ISibasiHTTPResponse<number>>(
      `${environment.api_url}/inventory/searched/count`,
      { searchParam }
    );
  }

  /* ####################################  INVENTORY DISTRIBUTION  #################################### */

  getAllInventoryDistribution(): Observable<ISibasiHTTPResponse<any[]>> {
    return this.httpClient.get<ISibasiHTTPResponse<any[]>>(
      `${environment.api_url}/inventory/distribution/all`
    );
  }

  getPaginatedInventoryDistribution(
    pagination?: any
  ): Observable<ISibasiHTTPResponse<any[]>> {
    return this.httpClient
      .post<ISibasiHTTPResponse<any[]>>(
        `${environment.api_url}/inventory/distribution/paginated`,
        pagination ? pagination : {}
      )
      .pipe(
        map((res) => {
          const inventoryDistribution =
            res.result?.length > 0 ? res.result.slice().reverse() : [];
          return {
            ...res,
            result: inventoryDistribution,
          };
        })
      );
  }

  getAllInventoryDistributionForOneInventory(
    inventoryId: string
  ): Observable<ISibasiHTTPResponse<any[]>> {
    return this.httpClient.post<ISibasiHTTPResponse<any[]>>(
      `${environment.api_url}/inventory/distribution/all/filtered`,
      {
        filterField: 'inventory',
        filterValue: inventoryId,
      }
    );
  }

  getPaginatedInventoryDistributionForOneInventory(
    pagination?: any
  ): Observable<ISibasiHTTPResponse<any[]>> {
    return this.httpClient
      .post<ISibasiHTTPResponse<any[]>>(
        `${environment.api_url}/inventory/distribution/paginated/filtered`,
        pagination ? pagination : {}
      )
      .pipe(
        map((res) => {
          const inventoryDistribution =
            res.result?.length > 0 ? res.result.slice().reverse() : [];
          return {
            ...res,
            result: inventoryDistribution,
          };
        })
      );
  }

  getOneInventoryDistributionById(
    inventoryDistributionId: string
  ): Observable<ISibasiHTTPResponse<any>> {
    return this.httpClient.get<ISibasiHTTPResponse<any>>(
      `${environment.api_url}/inventory/distribution/one/${inventoryDistributionId}`
    );
  }

  createInventoryDistribution(
    InventoryDistribution: any[]
  ): Observable<ISibasiHTTPResponse<any[]>> {
    return this.httpClient.post<ISibasiHTTPResponse<any[]>>(
      `${environment.api_url}/inventory/distribution`,
      InventoryDistribution
    );
  }

  updateInventoryDistribution(
    InventoryDistribution: Partial<any[]>,
    inventoryDistributionId: string
  ): Observable<ISibasiHTTPResponse<any[]>> {
    return this.httpClient.post<ISibasiHTTPResponse<any[]>>(
      `${environment.api_url}/inventory/distribution/update/${inventoryDistributionId}`,
      InventoryDistribution
    );
  }

  deleteInventoryDistribution(
    inventoryDistributionId: string
  ): Observable<ISibasiHTTPResponse<any>> {
    return this.httpClient.delete<ISibasiHTTPResponse<any>>(
      `${environment.api_url}/inventory/distribution/delete/${inventoryDistributionId}`
    );
  }

  getAllInventoryDistributionCount(): Observable<ISibasiHTTPResponse<number>> {
    return this.httpClient.get<ISibasiHTTPResponse<number>>(
      `${environment.api_url}/inventory/distribution/all/count`
    );
  }

  getAllSearchedInventoryDistributionCount(
    searchParam: string
  ): Observable<ISibasiHTTPResponse<number>> {
    return this.httpClient.post<ISibasiHTTPResponse<number>>(
      `${environment.api_url}/inventory/distribution/searched/count`,
      { searchParam }
    );
  }

  getAllInventoryDistributionCountForOneInventory(
    inventoryId: string
  ): Observable<ISibasiHTTPResponse<number>> {
    return this.httpClient.get<ISibasiHTTPResponse<number>>(
      `${environment.api_url}/inventory/distribution/all/count/${inventoryId}`
    );
  }

  getAllSearchedInventoryDistributionCountForOneInventory(
    inventoryId: string,
    searchParam: string
  ): Observable<ISibasiHTTPResponse<number>> {
    return this.httpClient.post<ISibasiHTTPResponse<number>>(
      `${environment.api_url}/inventory/distribution/searched/count/${inventoryId}`,
      { searchParam }
    );
  }

  /* ####################################  INVENTORY CATEGORY & INVENTORY ITEM CATEGORY #################################### */

  getAllInventoryCategories(): Observable<
    ISibasiHTTPResponse<IInventoryCategory[]>
  > {
    return this.httpClient.get<ISibasiHTTPResponse<IInventoryCategory[]>>(
      `${environment.api_url}/inventory/category/all`
    );
  }

  getInventoryCategoryById(
    inventoryCategoryId: string
  ): Observable<ISibasiHTTPResponse<IInventoryCategory>> {
    return this.httpClient.get<ISibasiHTTPResponse<IInventoryCategory>>(
      `${environment.api_url}/inventory/category/one/${inventoryCategoryId}`
    );
  }

  createInventoryCategory(
    InventoryCategory: IInventoryCategory
  ): Observable<ISibasiHTTPResponse<IInventoryCategory>> {
    return this.httpClient.post<ISibasiHTTPResponse<IInventoryCategory>>(
      `${environment.api_url}/inventory/category`,
      InventoryCategory
    );
  }

  updateInventoryCategory(
    InventoryCategory: Partial<IInventoryCategory>,
    inventoryCategoryId: string
  ): Observable<ISibasiHTTPResponse<IInventoryCategory>> {
    return this.httpClient.post<ISibasiHTTPResponse<IInventoryCategory>>(
      `${environment.api_url}/inventory/category/update/${inventoryCategoryId}`,
      InventoryCategory
    );
  }

  // deleteInventoryCategory(inventoryCategoryId: string): Observable<ISibasiHTTPResponse<IInventoryCategory>> {
  //   return this.httpClient.delete<ISibasiHTTPResponse<IInventoryCategory>>(`${environment.api_url}/inventory/category/delete/${inventoryCategoryId}`);
  // }

  deleteInventoryCategory(
    inventoryCategoryId: string
  ): Observable<ISibasiHTTPResponse<IInventoryCategory>> {
    return this.httpClient.delete<ISibasiHTTPResponse<IInventoryCategory>>(
      `${environment.api_url}/inventory/category/slateforDeletion/${inventoryCategoryId}`
    );
  }

  restoreDeletedInventoryCategory(
    inventoryCategoryId: string
  ): Observable<ISibasiHTTPResponse<IInventoryCategory>> {
    return this.httpClient.delete<ISibasiHTTPResponse<IInventoryCategory>>(
      `${environment.api_url}/inventory/category/unslateforDeletion/${inventoryCategoryId}`
    );
  }

  getDeletedInventoryCategory(): Observable<
    ISibasiHTTPResponse<IInventoryCategory>
  > {
    return this.httpClient.get<ISibasiHTTPResponse<IInventoryCategory>>(
      `${environment.api_url}/inventory/category/deleted/all`
    );
  }

  getAllInventoryItemCategories(): Observable<
    ISibasiHTTPResponse<IInventoryItemCategory[]>
  > {
    return this.httpClient.get<ISibasiHTTPResponse<IInventoryItemCategory[]>>(
      `${environment.api_url}/inventory/item/category/all`
    );
  }

  getAllInventoryItemCategoriesByInventoryItemId(
    inventoryItemId: string
  ): Observable<ISibasiHTTPResponse<IInventoryItemCategory[]>> {
    return this.httpClient.get<ISibasiHTTPResponse<IInventoryItemCategory[]>>(
      `${environment.api_url}/inventory/item/category/all/${inventoryItemId}`
    );
  }

  getInventoryItemCategoryById(
    inventoryItemCategoryId: string
  ): Observable<ISibasiHTTPResponse<IInventoryItemCategory>> {
    return this.httpClient.get<ISibasiHTTPResponse<IInventoryItemCategory>>(
      `${environment.api_url}/inventory/item/category/one/${inventoryItemCategoryId}`
    );
  }

  createInventoryItemCategory(
    inventorItemCategory: IInventoryItemCategory
  ): Observable<ISibasiHTTPResponse<IInventoryItemCategory>> {
    return this.httpClient.post<ISibasiHTTPResponse<IInventoryItemCategory>>(
      `${environment.api_url}/inventory/item/category/new`,
      inventorItemCategory
    );
  }

  updateInventoryItemCategory(
    inventorItemCategory: Partial<IInventoryItemCategory>,
    inventoryItemCategoryId: string
  ): Observable<ISibasiHTTPResponse<IInventoryCategory>> {
    return this.httpClient.post<ISibasiHTTPResponse<IInventoryCategory>>(
      `${environment.api_url}/inventory/item/category/update/${inventoryItemCategoryId}`,
      inventorItemCategory
    );
  }

  // deleteInventoryItemCategory(inventoryItemCategoryId: string): Observable<ISibasiHTTPResponse<IInventoryCategory>> {
  //   return this.httpClient.delete<ISibasiHTTPResponse<IInventoryCategory>>(`${environment.api_url}/inventory/item/category/delete/${inventoryItemCategoryId}`);
  // }

  deleteInventoryItemCategory(
    inventoryItemCategoryId: string
  ): Observable<ISibasiHTTPResponse<IInventoryCategory>> {
    return this.httpClient.delete<ISibasiHTTPResponse<IInventoryCategory>>(
      `${environment.api_url}/inventory/item/category/slateforDeletion/${inventoryItemCategoryId}`
    );
  }

  restoreDeletedInventoryItemCategory(
    inventoryItemCategoryId: string
  ): Observable<ISibasiHTTPResponse<IInventoryCategory>> {
    return this.httpClient.delete<ISibasiHTTPResponse<IInventoryCategory>>(
      `${environment.api_url}/inventory/item/category/unslateforDeletion/${inventoryItemCategoryId}`
    );
  }

  getDeletedInventoryItemCategory(): Observable<
    ISibasiHTTPResponse<IInventoryCategory>
  > {
    return this.httpClient.get<ISibasiHTTPResponse<IInventoryCategory>>(
      `${environment.api_url}/inventory/item/category/deleted/all`
    );
  }

  /* ####################################  INVENTORY ITEMS QUANTITY  #################################### */

  getAllInventoryItemQuantities(): Observable<
    ISibasiHTTPResponse<IInventoryItemQuantity[]>
  > {
    return this.httpClient.get<ISibasiHTTPResponse<IInventoryItemQuantity[]>>(
      `${environment.api_url}/inventory/item/quantity/all`
    );
  }

  getPaginatedInventoryItemQuantities(
    pagination?: any
  ): Observable<ISibasiHTTPResponse<IInventoryItemQuantity[]>> {
    return this.httpClient
      .post<ISibasiHTTPResponse<IInventoryItemQuantity[]>>(
        `${environment.api_url}/inventory/item/quantity/paginated`,
        pagination ? pagination : {}
      )
      .pipe(
        map((res) => {
          const inventoryItemQuantities =
            res.result?.length > 0 ? res.result.slice().reverse() : [];
          return {
            ...res,
            result: inventoryItemQuantities,
          };
        })
      );
  }

  getInventoryItemQuantityById(
    inventoryItemQuantityId: string
  ): Observable<ISibasiHTTPResponse<IInventoryItemQuantity>> {
    return this.httpClient.get<ISibasiHTTPResponse<IInventoryItemQuantity>>(
      `${environment.api_url}/inventory/item/quantity/one/${inventoryItemQuantityId}`
    );
  }

  createInventoryItemQuantity(
    inventoryItemQuantity: IInventoryItemQuantity
  ): Observable<ISibasiHTTPResponse<IInventoryItemQuantity>> {
    return this.httpClient
      .post<ISibasiHTTPResponse<IInventoryItemQuantity>>(
        `${environment.api_url}/inventory/item/quantity`,
        inventoryItemQuantity
      )
      .pipe(
        map((x) => {
          this.updatedInventoryItemQuantity.next(true);
          return x;
        })
      );
  }

  createBulkInventoryItemQuantity(
    inventoryItemQuantity: any
  ): Observable<ISibasiHTTPResponse<IInventoryItemQuantity>> {
    return this.httpClient.post<ISibasiHTTPResponse<IInventoryItemQuantity>>(
      `${environment.api_url}/inventory/item/quantity/bulk`,
      inventoryItemQuantity
    );
  }

  updateInventoryItemQuantity(
    inventoryItemQuantity: Partial<IInventoryItemQuantity>,
    inventoryItemQuantityId: string
  ): Observable<ISibasiHTTPResponse<IInventoryItemQuantity>> {
    return this.httpClient.post<ISibasiHTTPResponse<IInventoryItemQuantity>>(
      `${environment.api_url}/inventory/item/quantity/update/${inventoryItemQuantityId}`,
      inventoryItemQuantity
    );
  }

  // deleteInventoryItemQuantity(inventoryItemQuantityId: string): Observable<ISibasiHTTPResponse<IInventoryItemQuantity>> {
  //   return this.httpClient.delete<ISibasiHTTPResponse<IInventoryItemQuantity>>(`${environment.api_url}/inventory/item/quantity/delete/${inventoryItemQuantityId}`);
  // }

  deleteInventoryItemQuantity(
    inventoryItemQuantityId: string
  ): Observable<ISibasiHTTPResponse<IInventoryCategory>> {
    return this.httpClient.delete<ISibasiHTTPResponse<IInventoryCategory>>(
      `${environment.api_url}/inventory/item/quantity/slateforDeletion/${inventoryItemQuantityId}`
    );
  }

  restoreDeletedInventoryItemQuantity(
    inventoryItemQuantityId: string
  ): Observable<ISibasiHTTPResponse<IInventoryCategory>> {
    return this.httpClient.delete<ISibasiHTTPResponse<IInventoryCategory>>(
      `${environment.api_url}/inventory/item/quantity/unslateforDeletion/${inventoryItemQuantityId}`
    );
  }

  getDeletedInventoryItemQuantity(): Observable<
    ISibasiHTTPResponse<IInventoryCategory>
  > {
    return this.httpClient.get<ISibasiHTTPResponse<IInventoryCategory>>(
      `${environment.api_url}/inventory/item/quantity/deleted/all`
    );
  }

  getAllInventoryItemQuantitiesCount(
    countParams
  ): Observable<ISibasiHTTPResponse<number>> {
    return this.httpClient.post<ISibasiHTTPResponse<number>>(
      `${environment.api_url}/inventory/item/quantity/all/count`,
      countParams
    );
  }

  getAllSearchedInventoryItemQuantitiesCount(
    searchParam: any
  ): Observable<ISibasiHTTPResponse<number>> {
    return this.httpClient.post<ISibasiHTTPResponse<number>>(
      `${environment.api_url}/inventory/item/quantity/searched/count`,
      { searchParam }
    );
  }

  /* ####################################  INVENTORY FACILITY #################################### */

  getAllInventoryItemFacilityStock(
    inventoryItemId: string
  ): Observable<ISibasiHTTPResponse<{ facility: string; quantity: number }[]>> {
    return this.httpClient.get<
      ISibasiHTTPResponse<{ facility: string; quantity: number }[]>
    >(
      `${environment.api_url}/inventory/item/quantity/facility/${inventoryItemId}`
    );
  }

  getAllInventoryFacilityDistribution(
    facilityId: string
  ): Observable<ISibasiHTTPResponse<any[]>> {
    return this.httpClient.get<ISibasiHTTPResponse<any[]>>(
      `${environment.api_url}/inventory/item/all/facility/${facilityId}`
    );
  }

  getAllPatientDisbursementByPatientId(
    patientId: string
  ): Observable<ISibasiHTTPResponse<any[]>> {
    return this.httpClient.get<ISibasiHTTPResponse<any[]>>(
      `${environment.api_url}/inventory/item/quantity/all/disbursments/patient/${patientId}`
    );
  }
}
